/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { audioContext } from "./utils";
import AudioRecordingWorklet from "./worklets/audio-processing";
import VolMeterWorket from "./worklets/vol-meter";

import { createWorketFromSrc } from "./audioworklet-registry";
import EventEmitter from "eventemitter3";

function arrayBufferToBase64(buffer: ArrayBuffer) {
  var binary = "";
  var bytes = new Uint8Array(buffer);
  var len = bytes.byteLength;
  for (var i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

export class AudioRecorder extends EventEmitter {
  stream: MediaStream | undefined;
  audioContext: AudioContext | undefined;
  source: MediaStreamAudioSourceNode | undefined;
  recording: boolean = false;
  recordingWorklet: AudioWorkletNode | undefined;
  vuWorklet: AudioWorkletNode | undefined;

  private starting: Promise<void> | null = null;

  constructor(public sampleRate = 16000) {
    super();
  }

  async start() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error("Could not request user media");
    }

    // If already starting or started, wait for it
    if (this.starting) {
      return this.starting;
    }

    if (this.recording && this.audioContext && this.recordingWorklet) {
      // Already recording, return immediately
      return Promise.resolve();
    }

    this.starting = new Promise(async (resolve, reject) => {
      try {
        // Clean up any existing resources first
        if (this.source) {
          this.source.disconnect();
        }
        if (this.stream) {
          this.stream.getTracks().forEach((track) => track.stop());
        }

        this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        this.audioContext = await audioContext({ sampleRate: this.sampleRate });

        // Ensure AudioContext is running
        if (this.audioContext.state === 'suspended') {
          await this.audioContext.resume();
        }

        this.source = this.audioContext.createMediaStreamSource(this.stream);

        const workletName = "audio-recorder-worklet";
        const src = createWorketFromSrc(workletName, AudioRecordingWorklet);

        // Register the worklet module
        try {
          await this.audioContext.audioWorklet.addModule(src);
          // Wait for worklet to be fully initialized
          // We need to wait for the next event loop tick to ensure the worklet is ready
          await new Promise(resolve => {
            // Use requestAnimationFrame to ensure we're in the next frame
            requestAnimationFrame(() => {
              setTimeout(resolve, 100);
            });
          });
        } catch (error: any) {
          // If module is already registered, try to continue
          if (error?.message?.includes('already been registered') ||
            error?.message?.includes('Module with same name')) {
            // Worklet already registered, wait a bit and continue
            await new Promise(resolve => setTimeout(resolve, 100));
          } else {
            throw error;
          }
        }

        // Now create the worklet node
        this.recordingWorklet = new AudioWorkletNode(
          this.audioContext,
          workletName,
        );

        this.recordingWorklet.port.onmessage = async (ev: MessageEvent) => {
          // worklet processes recording floats and messages converted buffer
          const arrayBuffer = ev.data.data.int16arrayBuffer;

          if (arrayBuffer) {
            const arrayBufferString = arrayBufferToBase64(arrayBuffer);
            this.emit("data", arrayBufferString);
          }
        };
        this.source.connect(this.recordingWorklet);

        // vu meter worklet
        const vuWorkletName = "vu-meter";
        const vuSrc = createWorketFromSrc(vuWorkletName, VolMeterWorket);

        try {
          await this.audioContext.audioWorklet.addModule(vuSrc);
          // Wait for worklet to be fully initialized
          await new Promise(resolve => {
            requestAnimationFrame(() => {
              setTimeout(resolve, 100);
            });
          });
        } catch (error: any) {
          // If module is already registered, try to continue
          if (error?.message?.includes('already been registered') ||
            error?.message?.includes('Module with same name')) {
            await new Promise(resolve => setTimeout(resolve, 100));
          } else {
            throw error;
          }
        }

        this.vuWorklet = new AudioWorkletNode(this.audioContext, vuWorkletName);
        this.vuWorklet.port.onmessage = (ev: MessageEvent) => {
          this.emit("volume", ev.data.volume);
        };

        this.source.connect(this.vuWorklet);
        this.recording = true;
        this.starting = null;
        resolve();
      } catch (error) {
        this.starting = null;
        console.error('Error starting audio recorder:', error);
        reject(error);
      }
    });

    return this.starting;
  }

  stop() {
    // its plausible that stop would be called before start completes
    // such as if the websocket immediately hangs up
    const handleStop = () => {
      if (this.source) {
        this.source.disconnect();
        this.source = undefined;
      }
      if (this.recordingWorklet) {
        this.recordingWorklet.disconnect();
        this.recordingWorklet = undefined;
      }
      if (this.vuWorklet) {
        this.vuWorklet.disconnect();
        this.vuWorklet = undefined;
      }
      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop());
        this.stream = undefined;
      }
      this.recording = false;
    };
    if (this.starting) {
      this.starting.then(handleStop).catch(() => {
        // Even if start failed, try to clean up
        handleStop();
      });
      this.starting = null;
      return;
    }
    handleStop();
  }
}
